import { useEffect, useState } from 'react';
import { apiPost } from '../api/client';
import { useT } from '../i18n/LangProvider';
import { BrandMark, Icon } from '../ui/Icon';

type Purpose = 'invite' | 'reset';
interface Validation { valid: boolean; purpose: Purpose | null; name: string | null }

const COPY: Record<Purpose, { title: string; sub: string; cta: string }> = {
  invite: {
    title: 'Attiva il tuo account',
    sub: 'Scegli una password per accedere a Touristation Hub.',
    cta: 'Attiva account',
  },
  reset: {
    title: 'Reimposta la password',
    sub: 'Imposta una nuova password per il tuo account.',
    cta: 'Reimposta password',
  },
};

export function ResetPasswordScreen() {
  const t = useT();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  const [check, setCheck] = useState<Validation | null>(null);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    apiPost<Validation>('/auth/validate-reset-token', { token })
      .then(setCheck)
      .catch(() => setCheck({ valid: false, purpose: null, name: null }))
      .finally(() => setChecking(false));
  }, [token]);

  const purpose: Purpose = check?.purpose ?? 'reset';
  const copy = COPY[purpose];
  const canSubmit = password.length >= 8 && password === confirm && !submitting;

  async function submit() {
    setError(null);
    if (password.length < 8) { setError(t('La password deve avere almeno 8 caratteri.')); return; }
    if (password !== confirm) { setError(t('Le due password non coincidono.')); return; }
    setSubmitting(true);
    try {
      await apiPost('/auth/reset-password', { token, password });
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Operazione non riuscita.'));
    } finally {
      setSubmitting(false);
    }
  }

  const shell = (children: React.ReactNode) => (
    <div className="login-shell">
      <div className="card" style={{ maxWidth: 440, margin: 'auto', padding: 30 }}>
        <div className="row" style={{ gap: 12, alignItems: 'center', marginBottom: 18 }}>
          <BrandMark size={36} />
          <div style={{ fontWeight: 700 }}>Touristation Hub</div>
        </div>
        {children}
      </div>
    </div>
  );

  if (checking) {
    return shell(<div className="muted" style={{ fontSize: 13.5 }}>{t('Verifica del link…')}</div>);
  }

  if (!token || !check?.valid) {
    return shell(
      <>
        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', color: 'var(--danger)' }}>
          <Icon name="warn" size={20} />
          <div>
            <h1 className="h2" style={{ margin: '0 0 4px', fontSize: 17 }}>{t('Link non valido o scaduto')}</h1>
            <p className="muted" style={{ margin: 0, fontSize: 13.5 }}>
              {t('Il link potrebbe essere già stato usato o essere scaduto. Richiedine uno nuovo dalla pagina di accesso.')}
            </p>
          </div>
        </div>
        <a className="btn btn--brand" href="/" style={{ marginTop: 18 }}>{t('Vai al login')}</a>
      </>,
    );
  }

  if (done) {
    return shell(
      <>
        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', color: 'var(--ok)' }}>
          <Icon name="check" size={20} />
          <div>
            <h1 className="h2" style={{ margin: '0 0 4px', fontSize: 17 }}>{t('Password impostata')}</h1>
            <p className="muted" style={{ margin: 0, fontSize: 13.5 }}>{t('Ora puoi accedere con la tua nuova password.')}</p>
          </div>
        </div>
        <a className="btn btn--brand" href="/" style={{ marginTop: 18 }}>{t('Vai al login')}</a>
      </>,
    );
  }

  return shell(
    <>
      <h1 className="h2" style={{ margin: '0 0 4px', fontSize: 18 }}>{t(copy.title)}</h1>
      <p className="muted" style={{ margin: '0 0 18px', fontSize: 13.5 }}>
        {check.name ? `${t('Ciao')} ${check.name}, ` : ''}{t(copy.sub)}
      </p>

      <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="col" style={{ gap: 12 }}>
        <div className="field">
          <label className="label" htmlFor="rp-pwd">{t('Nuova password')}</label>
          <div className="login-password">
            <input
              id="rp-pwd" className="input" type={showPwd ? 'text' : 'password'} autoComplete="new-password"
              value={password} onChange={(e) => { setPassword(e.target.value); if (error) setError(null); }}
              minLength={8} required disabled={submitting}
            />
            <button type="button" className="login-password__toggle" onClick={() => setShowPwd((s) => !s)}
              aria-label={showPwd ? t('Nascondi password') : t('Mostra password')} aria-pressed={showPwd}>
              <Icon name={showPwd ? 'eyeOff' : 'eye'} size={16} />
            </button>
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="rp-confirm">{t('Conferma password')}</label>
          <input
            id="rp-confirm" className="input" type={showPwd ? 'text' : 'password'} autoComplete="new-password"
            value={confirm} onChange={(e) => { setConfirm(e.target.value); if (error) setError(null); }}
            minLength={8} required disabled={submitting}
          />
        </div>

        {password && password.length < 8 && (
          <div className="muted" style={{ fontSize: 12 }}>{t('Almeno 8 caratteri.')}</div>
        )}
        {error && (
          <div style={{ display: 'flex', gap: 7, alignItems: 'center', color: 'var(--danger)', fontSize: 13 }}>
            <Icon name="warn" size={14} /> {error}
          </div>
        )}

        <button type="submit" className="btn btn--brand" disabled={!canSubmit} style={{ marginTop: 4 }}>
          {submitting ? t('Salvataggio…') : t(copy.cta)}
        </button>
      </form>
    </>,
  );
}
